import { memo } from 'react'
import { Box, Stack, Typography, CircularProgress } from '@mui/material'
import { fmtAmount } from '../SalesReport/formatters'

const summaryCardSx = {
  flex: 1,
  borderRadius: 2,
  px: 2,
  py: 1.75,
  minHeight: 76,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 6px -1px rgba(0,0,0,0.1)',
}

// Admin POS promo report cards. Field purposes (from reports.service promo summary):
// - promotionalSales: sale-side promo/reimb gross only (not net of returns)
// - totalGrossPromoUnitsSold: units sold on promo parent lines
// - grossSales / netSales: promo-attributed sale $; net = gross − POS returns
// - totalSalesInvoices / totalReturnInvoices: invoice counts
// - totalPromoReturnUnits: units returned against promo parents
// - totalReimbursementAmount: grocer reimbursement owed =
//   sale vendor_reimbursement − return reimbursement reversals
//   (canonical "what grocer gets paid back"; mini maps its promo card here)
const CARDS = [
  {
    key: 'promotionalSales',
    label: 'Promotional Sales',
    format: (v) => fmtAmount(v),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #D1FAE5 100%)',
    color: '#047857',
  },
  {
    key: 'totalGrossPromoUnitsSold',
    label: 'Total gross promo units sold',
    format: (v) => Number(v || 0).toLocaleString(),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #E8F4FC 100%)',
    color: '#11809A',
  },
  {
    key: 'grossSales',
    label: 'Gross sales',
    format: (v) => fmtAmount(v),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #F4ECFF 100%)',
    color: '#6941C6',
  },
  {
    key: 'netSales',
    label: 'Net sales',
    format: (v) => fmtAmount(v),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #ECFDF5 100%)',
    color: '#059669',
  },
  {
    key: 'totalSalesInvoices',
    label: 'Total sales invoices',
    format: (v) => Number(v || 0).toLocaleString(),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FFF7ED 100%)',
    color: '#C2410C',
  },
  {
    key: 'totalPromoReturnUnits',
    label: 'Total promo return units',
    format: (v) => Number(v || 0).toLocaleString(),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FEF3C7 100%)',
    color: '#D97706',
  },
  {
    key: 'totalReturnInvoices',
    label: 'Total return invoices',
    format: (v) => Number(v || 0).toLocaleString(),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FEF2F2 100%)',
    color: '#DC2626',
  },
  {
    key: 'totalReimbursementAmount',
    label: 'Total Reimbursement Amount',
    format: (v) => fmtAmount(v),
    gradient: 'linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 100%)',
    color: '#0369A1',
  },
]

function PromoReportSummaryCards({ summary, loading }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
      {CARDS.map((card) => (
        <Box
          key={card.key}
          sx={{
            ...summaryCardSx,
            background: card.gradient,
            minWidth: { sm: 160 },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: card.color }} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#25314C', mb: 0.5 }}>
              {card.format(summary?.[card.key])}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            {card.label}
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}

export default memo(PromoReportSummaryCards)
